import {Box, Button, Card, Container, FileButton, Group, Modal, ModalProps, Stack, Text, Title} from "@mantine/core";
import {IconLayersDifference} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {TicketCompareDataTable} from "../../components/data-table/TicketCompareDataTable/TicketCompareDataTable";
import {useCallback, useMemo, useState} from "react";
import {notifications} from "@mantine/notifications";
import {
  CompanyEntity,
  firstOrUndefined,
  RateModelEntity,
  TtaCompareReportDownloadArg,
  TtaMappedPairEntity,
  ttaMappedParisSelector,
  useLazyMapTtasQuery
} from "@ots-frontend/ots-data";
import {TruckCompanySelect} from "../../components/select/entity/CompanySelect/TruckCompanySelect";
import {RateModelSelect} from "../../components/select/entity/RateModelSelect/RateModelSelect";
import {useDownloadCompareTtaReport} from "../../hooks/useDownloadTtaCompareReport";
import {useDisclosure} from "@mantine/hooks";
import {TicketMappingIdSelect} from "../../components/select/mapping/TicketMappingIdSelect/TicketMappingIdSelect";
import {useOtsUISelector} from "../../hooks/useOtsUISelector";
import {selectTicketComparePageCriteria} from "../../store/ui/ticket-compare-page/selectors";
import {setTicketComparePageCriteria} from "../../store/ui/ticket-compare-page/actions";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";

export type TtaCompareReportDownloadModalProps = ModalProps & {
  data: TtaMappedPairEntity[]
}

const TtaCompareReportDownloadModal = (props: TtaCompareReportDownloadModalProps) => {
  const {data, onClose, ...rest} = props

  const dispatch = useDispatch()
  const {regionId, baseQueryApi} = useApp();
  const {t} = useTranslation("common")

  const criteria = useOtsUISelector(selectTicketComparePageCriteria)

  const [rateModelId, setRateModelId] = useState<string | undefined>(criteria?.rateModelId)
  const [rateModelIdError, setRateModelIdError] = useState<boolean>(false)

  const {
    downloadCompareTtaReport,
    downloading,
  } = useDownloadCompareTtaReport({baseQueryApi})

  const onDownloadButtonClick = useCallback(() => {
    if (!rateModelId) {
      setRateModelIdError(true)
      return
    }

    setRateModelIdError(false)

    const arg: TtaCompareReportDownloadArg = {
      ttaMappedPairs: data?.map(p => p.plain()) ?? [],
      rateModelId: rateModelId,
      regionId: regionId,
    }

    downloadCompareTtaReport(arg, undefined)

  }, [data, rateModelId, regionId, downloadCompareTtaReport]);

  const onRateModelChange = useCallback((values: RateModelEntity[]) => {
    const value = firstOrUndefined(values)
    dispatch(setTicketComparePageCriteria({
      ...criteria,
      rateModelId: value ? value?.id : undefined,
    }))

    setRateModelId(value?.id)
  }, [dispatch, criteria])

  return (
    <Modal {...rest} onClose={onClose}>
      <Box>
        <RateModelSelect
          withinPortal={true}
          selector={{
            regionId: regionId
          }}
          onEntityChange={onRateModelChange}
          error={rateModelIdError}/>
        <Group position="right" my="md">
          <Button onClick={onDownloadButtonClick} loading={downloading}>
            {t("common.action.download", "Download")}
          </Button>
        </Group>
      </Box>
    </Modal>
  )
}

export type TicketComparePageProps = {

}

export const TicketComparePageRoute = () => {
  return <TicketComparePage/>;
};

export const TicketComparePage = (props: TicketComparePageProps) => {
  const {

  } = props;

  const dispatch = useDispatch()
  const {t} = useTranslation(["common"]);
  const {regionId} = useApp();

  const [trigger, result] = useLazyMapTtasQuery();

  const criteria = useOtsUISelector(selectTicketComparePageCriteria)

  const [file, setFile] = useState<File | null>(null)
  const [companyId, setCompanyId] = useState<string | undefined>(criteria?.companyId)
  const [companyIdError, setCompanyIdError] = useState<boolean>(false)
  const [mappingId, setMappingId] = useState<string | null>(criteria?.mappingId ? criteria.mappingId : null)
  const [mappingIdError, setMappingIdError] = useState<boolean>(false)

  const [opened, handlers] = useDisclosure(false);

  const selectTtaMappedPairs = useMemo(() => {
    return ttaMappedParisSelector();
  }, []);

  const ttaMappedPairs = useMemo(() => {
    return selectTtaMappedPairs(result)
  }, [selectTtaMappedPairs, result])

  const onSubmit = useCallback(() => {
    setCompanyIdError(!companyId)
    setMappingIdError(!mappingId)

    if (companyId && mappingId && file) {
      const arg = {
        mappingFileId: mappingId,
        companyId: companyId,
        file: file
      }

      trigger(arg)
        .unwrap()
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
          })
        );
    }
  }, [companyId, mappingId, file, trigger, t]);

  const onDownloadButtonClick = useCallback(() => {
    handlers.open()
  }, [handlers]);

  const onCompanyChange = useCallback((values: CompanyEntity[]) => {
    const value = firstOrUndefined(values)
    dispatch(setTicketComparePageCriteria({
      ...criteria,
      companyId: value ? value?.id : undefined,
    }))

    setCompanyId(value?.id)
  }, [dispatch, criteria])

  const onMappingChange = useCallback((value: string | null) => {
    dispatch(setTicketComparePageCriteria({
      ...criteria,
      mappingId: value ? value : undefined,
    }))

    setMappingId(value)
  }, [dispatch, criteria])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Group spacing="xs">
                      <IconLayersDifference/>
                      <Title order={4}>
                        {t("common.page.ticket.compare.title", "Ticket Compare")}
                      </Title>
                    </Group>
                  </Group>

                  <Group position="center" mb="lg">
                    <TruckCompanySelect
                      label={"Truck Company"}
                      placeholder={"Truck Company"}
                      withinPortal={true}
                      selector={{
                        regionId: regionId
                      }}
                      onEntityChange={onCompanyChange}
                      defaultValue={companyId}
                      error={companyIdError}/>
                    <TicketMappingIdSelect
                      label="Mapping"
                      placeholder="Mapping"
                      withinPortal={true}
                      onChange={onMappingChange}
                      defaultValue={mappingId}
                      error={mappingIdError}/>
                  </Group>
                </Card>

                <Group position="left">
                  <Text size="sm" align="center">
                    File: {file?.name ?? "No File Selected"}
                  </Text>
                </Group>

                <Group position="apart">
                  <Group position="left">
                    <FileButton onChange={setFile}>
                      {(props) => <Button {...props} variant="outline">Upload File</Button>}
                    </FileButton>
                    <Button onClick={onSubmit} disabled={!file}>
                      {t("common.action.submit", "Submit")}
                    </Button>
                  </Group>
                  <Button onClick={onDownloadButtonClick}>
                    {t("common.action.download", "Download")}
                  </Button>
                </Group>

                <TicketCompareDataTable
                  withBorder
                  withColumnBorders={true}
                  records={ttaMappedPairs}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={result.isLoading || result.isFetching}/>

                <TtaCompareReportDownloadModal
                  title={t<string>("common.action.download.report", "Download Report")}
                  opened={opened}
                  onClose={handlers.close}
                  data={ttaMappedPairs}/>

              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
